import {
  Seo,
  MerchandisingProductView,
  ContentfulCloudinaryImageComponent,
  Faq,
} from "../../types/ContentfulTypes";
import { LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { getVatInclusivityFromPcxtParam } from "../pricingContext";
import { Offer, Question, FAQPage, Product, Brand } from "schema-dts";
import { RatingReviewData, getAggregateRatingStructuredData } from "@vp/rating-reviews-component";
import { ProductBuilderOptions } from "../../types/ProductBuilderTypes";
import { getSelections } from "../defaultSelections";
import { MpvProvider } from "../MpvProvider";
import Quantity from "../Quantity";
import { getUpdatedState } from "../../state/updatedState";
import PageConfig from "../PageConfig";
import { constructVideoUrl } from "../ProductBuilderHelper";
import { CloudinaryVideoMedia } from "../../types/CloudinaryTypes";
import { minBy } from "lodash";

export interface GetProductQuestionsResponse {
  results: [
    {
      details: {
        nickname: string;
        text: string;
        location: string;
      };
      answer: [
        {
          details: {
            nickname: string;
            location: string;
            text: string;
          };
          metrics: {
            helpful_votes: number;
            not_helpful_votes: number;
          };
        }
      ];
      answer_count: number;
    }
  ];
}

export default class StructuredDataBuilder {
  private tenant: string;
  private mpv: MerchandisingProductView;
  private seo?: Seo;
  private url: string;
  private canonicalDomain: string;
  private defaultVatIncl: boolean;
  private locale: string;

  private get isTaxInclusive(): boolean {
    const pcxtVatInclusiveQueryParam = getVatInclusivityFromPcxtParam();
    if (pcxtVatInclusiveQueryParam !== undefined) {
      return pcxtVatInclusiveQueryParam;
    }

    return this.defaultVatIncl;
  }

  constructor(
    mpv: MerchandisingProductView,
    seo: Seo | undefined,
    url: string,
    canonicalDomain: string,
    defaultVatIncl: boolean,
    locale: string
  ) {
    this.tenant = PageConfig.tenant;
    this.mpv = mpv;
    this.seo = seo;
    this.url = url;
    this.canonicalDomain = canonicalDomain;
    this.defaultVatIncl = defaultVatIncl;
    this.locale = locale;
  }

  public product(
    startingAtPrice: LowestPrice[] | undefined,
    reviewsData: RatingReviewData,
    heroGallery: (ContentfulCloudinaryImageComponent | CloudinaryVideoMedia)[]
  ) {
    let aggregateRating = undefined;

    if (reviewsData) {
      aggregateRating = getAggregateRatingStructuredData(reviewsData);
    }

    let images: string[] = [];
    if (heroGallery && heroGallery.length > 0) {
      images = heroGallery
        .map(img => {
          const cloudinaryImage = img as ContentfulCloudinaryImageComponent;
          return (
            cloudinaryImage &&
            cloudinaryImage.cloudinaryImage &&
            cloudinaryImage.cloudinaryImage.cloudinaryUrl
          );
        })
        .filter(img => !!img) as string[];
    }

    const mpvProvider = new MpvProvider(this.mpv);
    const allOptionsAndValues = mpvProvider.getAllOptionsAndValues();

    const data: Product = {
      "@type": "Product",
      "@id": this.mpv.coreProductId,
      name: this.mpv.name,
      sku: this.mpv.coreProductId,
      mpn: this.mpv.coreProductId,
      description: this.seo && this.seo.metaDescription ? this.seo.metaDescription : "",
      brand: {
        "@type": "Brand",
        name: this.getTenant(),
      } as Brand,
      ...allOptionsAndValues,
    };

    if (startingAtPrice && startingAtPrice[0]) {
      data.offers = this.buildOffers(startingAtPrice);
    }

    if (images && images.length > 0) {
      data.image = images;
    }

    if (aggregateRating) {
      data.aggregateRating = aggregateRating;
    }

    return data;
  }

  public video(heroGallery: (ContentfulCloudinaryImageComponent | CloudinaryVideoMedia)[]) {
    const videoData = heroGallery.map(media => {
      const videoMedia = media as CloudinaryVideoMedia;
      if (videoMedia.video) {
        const videoUrl = constructVideoUrl(videoMedia);
        return {
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: videoMedia.videoName,
          description: videoMedia.description,
          thumbnailUrl: [`${videoUrl}.jpg`],
          uploadDate: videoMedia.createdAt,
          contentUrl: videoUrl,
        };
      }
    });
    return videoData.filter(video => !!video);
  }

  public faq(questions: Faq[]) {
    if (questions?.length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "FAQPage",
        mainEntity: this.buildQuestions(questions),
      } as FAQPage;
    }

    return undefined;
  }

  public constructGraph(product?: Product, faq?: FAQPage) {
    return {
      "@context": "http://schema.org",
      "@graph": [product, faq].filter(element => !!element),
    };
  }

  private buildOffers(startingAtPrice: LowestPrice[]): Offer {
    const isTaxInclusive = this.isTaxInclusive;
    const getDiscountedPrice = (sap: LowestPrice): number =>
      isTaxInclusive ? sap.totalDiscountedPrice.taxed : sap.totalDiscountedPrice.untaxed;
    const getListPrice = (sap: LowestPrice): number =>
      isTaxInclusive ? sap.totalListPrice.taxed : sap.totalListPrice.untaxed;
    const minStartingAtPrice = minBy(startingAtPrice, getDiscountedPrice);

    return {
      "@type": "Offer",
      url: this.url ? `${this.getDomainUrl()}${this.url}` : undefined,
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: this.getTenant(),
      },
      price: minStartingAtPrice && getDiscountedPrice(minStartingAtPrice),
      priceCurrency: minStartingAtPrice?.currency,
      priceQuantity: minStartingAtPrice?.quantity,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        priceType: "https://schema.org/StrikethroughPrice",
        price: minStartingAtPrice && getListPrice(minStartingAtPrice),
        priceCurrency: minStartingAtPrice?.currency,
        priceQuantity: minStartingAtPrice?.quantity,
      },
    } as Offer;
  }

  private getDomainUrl() {
    return `https://${this.canonicalDomain}`;
  }

  private getTenant() {
    if (typeof this.tenant === "string") {
      // Upper case the first char.
      return this.tenant.charAt(0).toUpperCase() + this.tenant.slice(1);
    }
    return "";
  }

  private buildQuestions(questions: Faq[]): Question[] {
    if (questions && questions.length > 0) {
      return questions
        .filter(q => q.answer && q.question)
        .map(q => {
          return {
            "@type": "Question",
            name: q.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: q.answer,
            },
          } as Question;
        });
    }
    return [];
  }
}

export interface StructuredDataPricesProps {
  productBuilderOptions: ProductBuilderOptions;
  mpv: MerchandisingProductView;
  locale: string;
  country: string;
  defaultVatIncl: boolean;
}

export const getStructuredDataPrices = async (
  props: StructuredDataPricesProps
): Promise<Record<number, LowestPrice>> => {
  const { productBuilderOptions, mpv, locale, country } = props;
  const selections = await getSelections({ productBuilderOptions, mpv, country });

  const mpvProvider = new MpvProvider(mpv);
  const selectedQuantity = Quantity.getDefaultQuantity(
    mpv.quantities.default,
    mpv.quantities.recommended
  );

  const updatedStateData = await getUpdatedState({
    locale,
    currentCompatibleRanges: mpvProvider.getAllRanges(),
    currentCompatibleValues: mpvProvider.getAllOptionsAndValues(),
    selectedQuantity,
    mpv,
    selections,
  });

  return updatedStateData.prices;
};
